import { useState, useCallback } from 'react';
import { message } from 'antd';
import { b255Api, FB650, FB651, FB652, ValidationError } from '../../../services/b255Api';
import type { B255Mode } from './useB255Mode';

const emptyMaster = (): Omit<FB650, 'details' | 'messages'> => ({
  fb650_01: '',
  fb650_07: '',
  fb650_10: '',
  fb650_11: null,
  fb650_12: '',
  fb650_13: '',
  fb650_14: '',
  fb650_15: '',
  fb650_16: 0,
  fb650_17: 0,
  fb650_30: '',
  fb650_31: '',
  fb650_41: 0,
  fb650_42: 1,
  fb650_80: 'N',
  fb650_81: '',
  fb650_82: null,
  fb650_90: 0,
  fb650_91: '',
  fb650_92: null,
  fb650_93: '',
});

const emptyDetail = (): Omit<FB651, 'fb651_01' | 'fb651_02'> => ({
  fb651_04: null,
  fb651_07: '',
  fb651_11: '',
  fb651_11a: '',
  fb651_12: '',
  fb651_13: 0,
  fb651_14: 0,
  fb651_15: 0,
  fb651_18: '',
  fb651_85: false,
  fb651_90: 0,
  fb651_91: '',
  fb651_93: '',
});

export function useB255Record(setMode: (m: B255Mode) => void) {
  const [record, setRecord] = useState<FB650 | null>(null);
  const [formData, setFormData] = useState<Omit<FB650, 'details' | 'messages'>>(emptyMaster());
  const [details, setDetails] = useState<Omit<FB651, 'fb651_01' | 'fb651_02'>[]>([]);
  const [messages, setMessages] = useState<Omit<FB652, 'fb652_01' | 'fb652_02'>[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await b255Api.get(id);
      setRecord(data);
      const { details: d, messages: m, ...master } = data;
      setFormData(master);
      setDetails((d || []).map(({ fb651_01: _o, fb651_02: _i, ...rest }) => rest));
      setMessages((m || []).map(({ fb652_01: _o, fb652_02: _i, ...rest }) => rest));
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const getField = useCallback(
    (field: keyof Omit<FB650, 'details' | 'messages'>) => formData[field] ?? '',
    [formData]
  );

  const setField = useCallback(
    (field: keyof Omit<FB650, 'details' | 'messages'>, value: unknown) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData(emptyMaster());
    setDetails([]);
    setMessages([]);
    setValidationErrors([]);
  }, []);

  const revertToRecord = useCallback(() => {
    if (!record) return;
    const { details: d, messages: m, ...master } = record;
    setFormData(master);
    setDetails((d || []).map(({ fb651_01: _o, fb651_02: _i, ...rest }) => rest));
    setMessages((m || []).map(({ fb652_01: _o, fb652_02: _i, ...rest }) => rest));
  }, [record]);

  const addDetailRow = useCallback(() => {
    setDetails(prev => [...prev, emptyDetail()]);
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateDetailField = useCallback(
    (idx: number, field: keyof Omit<FB651, 'fb651_01' | 'fb651_02'>, value: unknown) => {
      setDetails(prev => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [field]: value };
        // Auto-calc amount
        if (field === 'fb651_13' || field === 'fb651_14') {
          const qty = field === 'fb651_13' ? (value as number) : updated[idx].fb651_13;
          const price = field === 'fb651_14' ? (value as number) : updated[idx].fb651_14;
          updated[idx].fb651_15 = Math.round(qty * price);
        }
        return updated;
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    setLoading(true);
    setValidationErrors([]);
    try {
      const req = { master: formData, details, messages };
      let saved: FB650;
      if (!record) {
        saved = await b255Api.create(req);
      } else {
        saved = await b255Api.update(record.fb650_01, req);
      }
      setRecord(saved);
      const { details: d, messages: m, ...master } = saved;
      setFormData(master);
      setDetails((d || []).map(({ fb651_01: _o, fb651_02: _i, ...rest }) => rest));
      setMessages((m || []).map(({ fb652_01: _o, fb652_02: _i, ...rest }) => rest));
      setMode('browse');
      message.success('存檔成功');
    } catch (err: unknown) {
      if (
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { status?: number; data?: { errors?: ValidationError[] } } }).response?.status === 422
      ) {
        const vErrs = (err as { response: { data: { errors: ValidationError[] } } }).response.data.errors;
        setValidationErrors(vErrs);
        message.error(vErrs.map(e => e.message).join('\n'));
      } else {
        message.error('存檔失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, messages, record, setMode]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    setLoading(true);
    try {
      await b255Api.delete(record.fb650_01);
      setRecord(null);
      setFormData(emptyMaster());
      setDetails([]);
      setMessages([]);
      setMode('browse');
      message.success('刪除成功');
    } catch {
      message.error('刪除失敗，此單已核准或不存在');
    } finally {
      setLoading(false);
    }
  }, [record, setMode]);

  return {
    record,
    formData,
    details,
    messages,
    loading,
    validationErrors,
    loadRecord,
    getField,
    setField,
    resetForAdd,
    revertToRecord,
    addDetailRow,
    deleteDetailRow,
    updateDetailField,
    handleSave,
    handleDelete,
  };
}
